import { createFileRoute } from "@tanstack/react-router"

import { CodeBlock } from "@/components/dev/CodeBlock"
import { PropsTable } from "@/components/dev/PropsTable"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"

export const Route = createFileRoute(
  "/qr/$qrLookupID/dev/organisms/navigation"
)({
  staticData: {
    pageName: "Navigation Components",
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <>
      <style>{`
        .dev-container > *:not(:first-child) {
          margin-top: var(--space-5);
        }
        .screenshot-placeholder {
          background: var(--neutral-4);
          border: 2px dashed var(--neutral-7);
          border-radius: var(--radius-2);
          padding: var(--space-6);
          text-align: center;
          color: var(--neutral-11);
          margin: var(--space-3) 0;
        }
      `}</style>
      <div className="dev-container">
        <Card>
          <Heading level={1} as={2}>
            Navigation Components
          </Heading>
          <Text>
            The navigation system consists of three main components that work
            together to provide consistent navigation throughout the
            application: TopNavigationBar, Drawer, and Footer.
          </Text>
          <Text>
            Note: These components are already rendered in the app layout and
            are tightly integrated with routing, authentication, and theming
            systems.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            TopNavigationBar Component
          </Heading>
          <Text>
            The TopNavigationBar appears at the top of every page and provides
            branding and access to the main menu drawer. It adapts based on the
            current page and theme configuration.
          </Text>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-4)",
            }}
          >
            <div>
              <Text level={2} className="semi-bold">
                Default Navigation Bar
              </Text>
              <Text level={3}>
                Shows the logo (InFlow or white-label) and a hamburger menu
                button
              </Text>
              <div className="screenshot-placeholder">
                <Text>
                  Check the top of this page to see the navigation bar
                </Text>
              </div>
              <CodeBlock>{`import TopNavigationBar from "@/globalComponents/organisms/navigation/TopNavigationBar"

function Layout() {
  return (
    <>
      <TopNavigationBar />
      {/* Page content */}
    </>
  )
}`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Welcome Page Variant
              </Text>
              <Text level={3}>
                On the welcome page, the navigation bar displays:
              </Text>
              <ul style={{ marginLeft: "var(--space-5)" }}>
                <li>
                  <Text>A prominent logo card at the top</Text>
                </li>
                <li>
                  <Text>Welcome heading with location name</Text>
                </li>
                <li>
                  <Text>Clickable address that links to Google Maps</Text>
                </li>
                <li>
                  <Text>Hamburger menu button</Text>
                </li>
              </ul>
              <Text level={3}>
                The welcome variant is automatically shown when the URL path
                includes <code>/welcome</code> and white-label theming is
                active.
              </Text>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Key Features
              </Text>
              <ul style={{ marginLeft: "var(--space-5)" }}>
                <li>
                  <Text>
                    Automatically displays white-label logo when available
                  </Text>
                </li>
                <li>
                  <Text>Logo links back to the welcome page</Text>
                </li>
                <li>
                  <Text>
                    Hamburger button opens the navigation drawer with proper
                    ARIA labels
                  </Text>
                </li>
                <li>
                  <Text>
                    Uses theme configuration for custom welcome messages
                  </Text>
                </li>
                <li>
                  <Text>
                    Address supports coordinates, placeID, or string-based
                    location
                  </Text>
                </li>
              </ul>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Drawer Component
          </Heading>
          <Text>
            The Drawer is a slide-out navigation panel that provides access to
            all major sections of the app. It automatically adjusts content
            based on authentication state and theme configuration.
          </Text>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-4)",
            }}
          >
            <div>
              <Text level={2} className="semi-bold">
                Opening the Drawer
              </Text>
              <Text level={3}>
                Click the hamburger menu in the top navigation to open the
                drawer and explore its contents.
              </Text>
              <div className="screenshot-placeholder">
                <Text>
                  Click the hamburger menu icon at the top of this page to see
                  the drawer
                </Text>
              </div>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Drawer Content
              </Text>
              <Text level={3}>The drawer includes:</Text>
              <ul style={{ marginLeft: "var(--space-5)" }}>
                <li>
                  <Text>
                    <strong>Header:</strong> Personalized greeting (with user's
                    first name if authenticated)
                  </Text>
                </li>
                <li>
                  <Text>
                    <strong>Main Links:</strong> Home, Rates, Help, Policies,
                    Languages
                  </Text>
                </li>
                <li>
                  <Text>
                    <strong>Authenticated Links:</strong> Account, Vehicles
                    (only shown when signed in)
                  </Text>
                </li>
                <li>
                  <Text>
                    <strong>Footer:</strong> Sign In/Create Account buttons or
                    Sign Out button
                  </Text>
                </li>
                <li>
                  <Text>
                    <strong>App Info:</strong> Logged-in email and app version
                  </Text>
                </li>
              </ul>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Language Switcher
              </Text>
              <Text level={3}>
                The drawer includes a language selection button that shows the
                current language with a flag icon. Clicking it opens a modal
                with all available languages.
              </Text>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Usage
              </Text>
              <Text level={3}>
                The Drawer is controlled by the TopNavigationBar and doesn't
                need to be used directly. However, if you need to use it
                independently:
              </Text>
              <CodeBlock>{`import { useState } from "react"
import Drawer from "@/globalComponents/organisms/navigation/Drawer"

function MyComponent() {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button onClick={() => setIsOpen(true)}>
        Open Drawer
      </button>
      <Drawer isOpen={isOpen} onOpenChange={setIsOpen} />
    </>
  )
}`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Key Features
              </Text>
              <ul style={{ marginLeft: "var(--space-5)" }}>
                <li>
                  <Text>Automatically closes when navigation occurs</Text>
                </li>
                <li>
                  <Text>
                    Links adapt to scanned area context (includes qrLookupID
                    when available)
                  </Text>
                </li>
                <li>
                  <Text>
                    Conditionally renders authenticated vs. unauthenticated
                    sections
                  </Text>
                </li>
                <li>
                  <Text>Full keyboard and screen reader accessibility</Text>
                </li>
                <li>
                  <Text>
                    Shows current environment (dev, staging) in non-production
                    builds
                  </Text>
                </li>
              </ul>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Footer Component
          </Heading>
          <Text>
            The Footer appears at the bottom of pages and provides links to
            legal documents and copyright information.
          </Text>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-4)",
            }}
          >
            <div>
              <Text level={2} className="semi-bold">
                Footer Content
              </Text>
              <ul style={{ marginLeft: "var(--space-5)" }}>
                <li>
                  <Text>InFlow logo</Text>
                </li>
                <li>
                  <Text>Links to Privacy Policy and Terms of Use</Text>
                </li>
                <li>
                  <Text>Copyright notice with current year</Text>
                </li>
              </ul>
              <div className="screenshot-placeholder">
                <Text>Scroll to the bottom of this page to see the footer</Text>
              </div>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Usage
              </Text>
              <CodeBlock>{`import Footer from "@/globalComponents/organisms/navigation/Footer"

function Layout() {
  return (
    <>
      {/* Page content */}
      <Footer />
    </>
  )
}`}</CodeBlock>
            </div>

            <div>
              <Text level={2} className="semi-bold">
                Context Awareness
              </Text>
              <Text level={3}>
                The Footer automatically preserves routing context when linking
                to legal pages. If you're viewing content within a scanned area
                (qrLookupID), that context is maintained in the links so users
                can navigate back.
              </Text>
            </div>
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            TopNavigationBar Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "N/A",
                type: "No props",
                default: "N/A",
                description:
                  "TopNavigationBar is a self-contained component that uses hooks to access theme, routing, and auth state",
              },
            ]}
          />
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Drawer Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "isOpen",
                type: "boolean",
                default: "Required",
                description: "Controls whether the drawer is open or closed",
              },
              {
                name: "onOpenChange",
                type: "(isOpen: boolean) => void",
                default: "Required",
                description: "Callback when drawer open state changes",
              },
              {
                name: "isDismissable",
                type: "boolean",
                default: "true",
                description:
                  "Whether clicking outside or pressing Escape closes the drawer",
              },
            ]}
          />
          <Text>
            Note: Drawer extends React Aria ModalOverlayProps, so it supports
            all standard modal overlay props.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Footer Props
          </Heading>
          <PropsTable
            props={[
              {
                name: "N/A",
                type: "No props",
                default: "N/A",
                description:
                  "Footer is a self-contained component that uses hooks to access routing context",
              },
            ]}
          />
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Integration Notes
          </Heading>
          <Text>
            These navigation components are designed to work together as part of
            the app's layout:
          </Text>
          <ul style={{ marginLeft: "var(--space-5)" }}>
            <li>
              <Text>
                <strong>TopNavigationBar</strong> should be placed at the top of
                your layout
              </Text>
            </li>
            <li>
              <Text>
                <strong>Drawer</strong> is automatically rendered by
                TopNavigationBar
              </Text>
            </li>
            <li>
              <Text>
                <strong>Footer</strong> should be placed at the bottom of your
                layout
              </Text>
            </li>
          </ul>
          <CodeBlock>{`import TopNavigationBar from "@/globalComponents/organisms/navigation/TopNavigationBar"
import Footer from "@/globalComponents/organisms/navigation/Footer"

function RootLayout() {
  return (
    <>
      <TopNavigationBar />
      <main>
        {/* Your page content */}
      </main>
      <Footer />
    </>
  )
}`}</CodeBlock>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Dependencies
          </Heading>
          <Text>These navigation components rely on several app systems:</Text>
          <ul style={{ marginLeft: "var(--space-5)" }}>
            <li>
              <Text>
                <strong>useTheme hook:</strong> Provides theme configuration and
                white-label settings
              </Text>
            </li>
            <li>
              <Text>
                <strong>useQuery (meQueryOptions):</strong> Fetches current user
                authentication state
              </Text>
            </li>
            <li>
              <Text>
                <strong>TanStack Router:</strong> Handles navigation and route
                matching
              </Text>
            </li>
            <li>
              <Text>
                <strong>i18next:</strong> Provides translations for all text
                content
              </Text>
            </li>
            <li>
              <Text>
                <strong>React Aria Components:</strong> Provides accessible
                modal/dialog primitives
              </Text>
            </li>
          </ul>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Theme Integration
          </Heading>
          <Text>
            The navigation components are fully integrated with the theming
            system:
          </Text>
          <ul style={{ marginLeft: "var(--space-5)" }}>
            <li>
              <Text>
                White-label logos are automatically displayed when configured
              </Text>
            </li>
            <li>
              <Text>Welcome page customization via theme configuration</Text>
            </li>
            <li>
              <Text>
                Location-specific information (name, address, coordinates)
              </Text>
            </li>
            <li>
              <Text>UI scheme (light/dark) is respected throughout</Text>
            </li>
          </ul>
        </Card>
      </div>
    </>
  )
}
